import { createElement } from 'react';

import {
  AllGeoJSON,
  type Feature,
  FeatureCollection,
  type Geometry,
  Position,
  area,
  centroid,
  circle,
  distance,
  length // @ts-ignore
} from '@turf/turf';
import { cloneDeep } from 'lodash';
import { MapOptions } from 'mapbox-gl';
import proj4 from 'proj4';

import { AddPoint, AddPointProps } from '../AddPoint';
import { DEFAULT_MAP_STATE, GLOBAL_GEOMETRY_TYPES } from '../constants';

export function getformattedArea(area: number, toFixed: number = 2) {
  const unit = 'km';
  const formattedArea =
    toFixed === 0 ? Math.floor(area) : area.toFixed(toFixed);

  return { unit, formattedArea };
}

export function createFormattedAreaEl(area: number, toFixed: number = 2) {
  const { unit, formattedArea } = getformattedArea(area, toFixed);

  return createElement(
    'div',
    {},
    createElement('span', {}, formattedArea),
    createElement('span', {}, ` ${unit}`),
    createElement('sup', {}, '2')
  );
}

export function createAddPointOptions(props: AddPointProps) {
  return createElement(AddPoint, { ...props });
}

export function getClosestDistanceBetweenFeatures(
  coords1: Position[],
  coords2: Position[]
) {
  let minDist = Infinity;
  let closestPair: [Position, Position] = [coords1[0], coords2[0]];

  for (let i = 0; i < coords1.length; i++) {
    for (let j = 0; j < coords2.length; j++) {
      if (!coords1[i] || !coords2[j]) continue;

      const dist = distance(coords1[i], coords2[j], { units: 'kilometers' });

      if (dist < minDist) {
        minDist = dist;
        closestPair = [coords1[i], coords2[j]];
      }
    }
  }

  return { minDist, closestPair };
}

function getUTMZone(longitude: number) {
  return Math.min(Math.max(Math.floor((longitude + 180) / 6) + 1, 1), 60);
}

function getTargetEPSGCode(geojson: AllGeoJSON) {
  const feature = centroid(geojson);
  const [lng, lat] = feature.geometry.coordinates;
  const utmZone = getUTMZone(lng);

  // Check if the lat is in the northern or southern hemisphere
  return `EPSG:${(lat >= 0 ? 32600 : 32700) + utmZone}`;
}

function projectCoords(coords: Position[] | Position[][], targetCRS: string) {
  const wgs84 = 'EPSG:4326'; // WGS84

  if (typeof coords[0][0] === 'number') {
    return coords.map((coord: Position) => proj4(wgs84, targetCRS, coord));
  }

  return projectCoords(coords[0], targetCRS);
}

function convertWGS84toUTM(geometry: Geometry) {
  const targetCRS = getTargetEPSGCode(geometry);

  return projectCoords(
    geometry.coordinates[0] as Position[] | Position[][],
    targetCRS
  );
}

function getUtmArea(geometry: Geometry): number {
  const vertices: Position[] = convertWGS84toUTM(cloneDeep(geometry));

  const n = vertices.length;
  let area = 0;

  // Loop through each vertex
  for (let i = 0; i < n; i++) {
    const x1 = vertices[i][0];
    const y1 = vertices[i][1];
    const x2 = vertices[(i + 1) % n][0]; // Next vertex, wrapping around to the first
    const y2 = vertices[(i + 1) % n][1];

    // Apply the shoelace formula
    area += x1 * y2 - y1 * x2;
  }

  // Return the absolute value of the area divided by 2
  return Math.abs(area) / 2 / 1e6; // Area in km2
}

export function getPolygonArea(...params: Parameters<typeof area>) {
  return area(...params) / 1e6; // Area in km2
}

getPolygonArea.utm = getUtmArea;

export function getLatLngInWsg84(input: string) {
  // Function to check if input is valid and complete coordinates (latitude, longitude)
  function isWgs84Format() {
    const regex = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/;
    return regex.test(input);
  }

  function isDmsFormat() {
    const r = input.replace(',', '');

    const r1 = /(\d+)°(\d+)'(\d+(\.\d+)?)"([NSEW])/;
    const r2 =
      /([0-9]+°[0-9]+'[0-9]+(\.[0-9]+)?"[NSEW])\s*([0-9]+°[0-9]+'[0-9]+(\.[0-9]+)?"[NSEW])/;

    return r1.test(r) || r2.test(r);
  }

  function dmsToDecimal(degrees: number, minutes: number, seconds: number) {
    return degrees + minutes / 60 + seconds / 3600;
  }

  function dmsToWgs84(): number[] {
    const parsedInput = input
      .replace(',', '')
      .match(
        /([0-9]+°[0-9]+'[0-9]+(\.[0-9]+)?"[NSEW])\s*([0-9]+°[0-9]+'[0-9]+(\.[0-9]+)?"[NSEW])/
      );

    if (!parsedInput) return [];

    const result = [parsedInput[1], parsedInput[3]];

    return result.map(coordinate => {
      const regex = /(\d+)°(\d+)'(\d+(\.\d+)?)"([NSEW])/;
      const matches = coordinate.match(regex);

      if (!matches) {
        throw new Error('Invalid DMS format');
      }

      const degrees = parseInt(matches[1], 10);
      const minutes = parseInt(matches[2], 10);
      const seconds = parseFloat(matches[3]);
      const direction = matches[5];

      let decimal = dmsToDecimal(degrees, minutes, seconds);

      // Handle direction
      if (direction === 'S' || direction === 'W') decimal = -decimal;

      return Number(decimal.toFixed(6));
    });
  }

  function getLatLng(): number[] {
    let latlng: number[] = [];

    if (isWgs84Format()) {
      latlng = input.replace(' ', '').split(',').map(Number);
    } else if (isDmsFormat()) {
      latlng = dmsToWgs84();
    }

    return latlng;
  }

  return { isValid: isWgs84Format() || isDmsFormat(), getLatLng };
}

export function isValidWgs84Range([lat, lng]: [number, number]) {
  function isValidLatRange(lat: number) {
    return lat >= -90 && lat <= 90;
  }

  function isValidLngRange(lng: number) {
    return lng >= -180 && lng <= 180;
  }

  return isValidLatRange(lat) && isValidLngRange(lng);
}

export const isMultiPolygon = (geojson: FeatureCollection) => {
  return geojson.features.length > 1;
};

export const isEmptyPolygon = (geojson: FeatureCollection) => {
  return geojson.features.length === 0;
};

export const isUnsupportedPolygonType = (geojson: FeatureCollection) => {
  return geojson.features.some(
    // @ts-ignore
    d => !GLOBAL_GEOMETRY_TYPES.includes(d.geometry.type)
  );
};

export function circleFromTwoVertexLineString(geojson: Feature) {
  const center = (geojson.geometry as Geometry).coordinates[0] as number[];
  const radiusInKm = length(geojson);

  return circle(center, radiusInKm);
}

export function findBottomRightVertex(
  feature: Feature
): [number, number] | null {
  let maxY: number | null = null;
  let maxX: number | null = null;

  function updateXY(coords: Array<[number, number]>) {
    if (coords.some(d => d === null)) return;

    const sorted = coords.sort((a, b) => b[0] - a[0]);

    maxY = Math.min(sorted[0][1], sorted[1][1]);
    maxX = sorted[0][1] === maxY ? sorted[0][0] : sorted[1][0];
  }

  const { geometry } = feature;

  // Check for Polygon or MultiPolygon
  if (geometry.type === 'Polygon' || geometry.type === 'LineString') {
    [...(geometry.coordinates as [number, number][][])].forEach(
      (polygon: Array<[number, number]>) => {
        updateXY([...polygon]);
      }
    );
  }
  // else if (geometry.type === 'MultiPolygon') {
  //   geometry.coordinates.forEach(multiPolygon => {
  //     (multiPolygon).forEach(polygon => {
  //       polygon.forEach((coord: Position[]) => {
  //         updateBottomRight(coord);
  //       });
  //     });
  //   });
  // }

  return maxX !== null && maxY !== null ? [maxX, maxY] : null;
}

export const _mapBoxDrawStyles = ({
  rsColorForegoundAccentPrimary,
  rsColorBackgroundBasePrimary
}: {
  rsColorForegoundAccentPrimary: string;
  rsColorBackgroundBasePrimary: string;
}): MapboxDraw.MapboxDrawOptions['styles'] =>
  [
    // Fill color for the drawn area
    {
      id: 'gl-draw-polygon-fill',
      type: 'fill',
      paint: {
        'fill-color': 'transparent',
        'fill-opacity': 0
      }
    },
    // Border color for the drawn area
    {
      id: 'gl-draw-polygon-stroke',
      type: 'line',
      paint: {
        'line-color': rsColorForegoundAccentPrimary, // Red border
        'line-width': 3
      }
    },
    {
      id: 'gl-draw-line',
      type: 'line',
      filter: ['==', '$type', 'LineString'],
      paint: {
        'line-color': rsColorForegoundAccentPrimary, // Color for drawn lines
        'line-width': 3
      }
    },
    {
      id: 'gl-draw-polygon-and-line-vertex-halo-active',
      type: 'circle',
      filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
      paint: {
        'circle-radius': 5,
        'circle-color': rsColorBackgroundBasePrimary,
        'circle-stroke-width': 3,
        'circle-stroke-color': rsColorForegoundAccentPrimary
      }
    },
    {
      id: 'gl-draw-polygon-and-line-vertex-active',
      type: 'circle',
      filter: [
        'all',
        ['==', 'meta', 'vertex'],
        ['==', '$type', 'Point'],
        ['==', 'active', 'true']
      ],
      paint: {
        'circle-radius': 5,
        'circle-color': rsColorBackgroundBasePrimary
      }
    }
  ] as const;

export const _initialMapConfig = (
  container: HTMLElement,
  accessToken: string,
  enableDevTools: boolean = true
): MapOptions => ({
  container,
  accessToken: accessToken,
  attributionControl: false,
  center: DEFAULT_MAP_STATE.center,
  zoom: DEFAULT_MAP_STATE.zoom,
  minZoom: DEFAULT_MAP_STATE.minZoom,
  doubleClickZoom: false,
  projection: { name: 'mercator' },
  devtools: enableDevTools
});
